import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CustomFieldConfig } from '../../types/category';
import { AssetType } from '../../types/asset';

interface CategoryCustomFieldsProps {
  customFields: CustomFieldConfig[];
  assetTypes: AssetType[];
  onChange: (fields: CustomFieldConfig[]) => void;
  disabled?: boolean;
}

export const CategoryCustomFields: React.FC<CategoryCustomFieldsProps> = ({
  customFields,
  assetTypes,
  onChange,
  disabled = false
}) => {
  const [editingFields, setEditingFields] = useState<CustomFieldConfig[]>(customFields);

  // Sync local state with props when they change
  useEffect(() => {
    setEditingFields(customFields);
  }, [customFields]);

  const handleAddField = () => {
    const newField: CustomFieldConfig = {
      fieldId: `field_${Date.now()}`,
      fieldName: '',
      assetTypeId: '',
      required: false
    };
    const updated = [...editingFields, newField];
    setEditingFields(updated);
    onChange(updated);
  };

  const handleRemoveField = (index: number) => {
    const updated = editingFields.filter((_, i) => i !== index);
    setEditingFields(updated);
    onChange(updated);
  };

  const handleFieldChange = (index: number, field: Partial<CustomFieldConfig>) => {
    const updated = editingFields.map((f, i) => 
      i === index ? { ...f, ...field } : f
    );
    setEditingFields(updated);
    onChange(updated);
  };

  // Filter only active asset types
  const activeAssetTypes = assetTypes.filter(at => at.isActive);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base">Campos de Activos</Label>
          <p className="text-sm text-slate-500">
            Configura qué tipos de activos se pueden asociar a gastos de esta categoría
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddField}
          disabled={disabled || activeAssetTypes.length === 0}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Campo
        </Button>
      </div>

      {activeAssetTypes.length === 0 && (
        <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-md">
          No hay tipos de activos disponibles. Crea tipos de activos primero en la pestaña de Activos.
        </div>
      )}

      {editingFields.length === 0 && activeAssetTypes.length > 0 && (
        <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-md">
          No hay campos configurados. Los gastos de esta categoría no requerirán activos asociados.
        </div>
      )}

      {editingFields.map((field, index) => {
        const selectedAssetType = assetTypes.find(at => at.assetTypeId === field.assetTypeId);
        
        return (
          <div key={field.fieldId} className="flex gap-3 items-start p-4 border rounded-lg bg-slate-50">
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`field-name-${index}`}>Nombre del Campo</Label>
                  <Input
                    id={`field-name-${index}`}
                    value={field.fieldName}
                    onChange={(e) => handleFieldChange(index, { fieldName: e.target.value })}
                    placeholder="ej: Vehículo, Empleado"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label htmlFor={`asset-type-${index}`}>Tipo de Activo</Label>
                  <Select
                    value={field.assetTypeId}
                    onValueChange={(value) => handleFieldChange(index, { assetTypeId: value })}
                    disabled={disabled}
                  >
                    <SelectTrigger id={`asset-type-${index}`}>
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeAssetTypes.map((assetType) => (
                        <SelectItem key={assetType.assetTypeId} value={assetType.assetTypeId}>
                          {assetType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`required-${index}`}
                  checked={field.required}
                  onChange={(e) => handleFieldChange(index, { required: e.target.checked })}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <Label htmlFor={`required-${index}`} className="font-normal cursor-pointer">
                  Campo obligatorio
                </Label>
              </div>

              {selectedAssetType && (
                <div className="text-xs text-slate-500">
                  Los usuarios podrán seleccionar activos del tipo "{selectedAssetType.name}"
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveField(index)}
              disabled={disabled}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};
